import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Share2, MapPin, ChevronDown, Download, MessageCircle } from 'lucide-react';
import html2canvas from 'html2canvas';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const t = {
  tag: 'BACHAT CALCULATOR',
  title: 'Smart Banao.',
  titleAccent: 'Zyada Bachao.',
  subtitle: 'Dekhiye Lion Cement chunke aap kitna bacha sakte hain market alternatives ki tulna mein.',
  bags: 'Bags ki Sankhya',
  currentChoice: 'Abhi ka Cement',
  location: 'Aapka District',
  selectDistrict: 'District Chuniye',
  calculate: 'Bachat Dekhein',
  youSave: 'Aap Bachate Hain',
  extraBags: 'Aur Bags Jo Aap Le Sakte Hain',
  shareWhatsApp: 'WhatsApp pe Share Karein',
  downloadCard: 'Card Download Karein',
  findDealer: 'Dealer Dhundhein',
  premium: 'Premium Cement',
  popular: 'Popular Cement',
  economy: 'Standard Cement',
  disclaimer: 'Result anumaan hain aur usage, conditions aur location ke anusar alag ho sakte hain.',
  pricePerBag: 'per bag',
  lionPrice: 'Lion Cement ka Daam',
  compPrice: 'Alternative ka Daam',
};

const SavingsCalculatorPage = () => {
  const [bags, setBags] = useState(100);
  const [competitor, setCompetitor] = useState('premium');
  const [districts, setDistricts] = useState({});
  const [location, setLocation] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const shareCardRef = useRef(null);

  useEffect(() => {
    trackPageView('/savings-calculator', 'Savings Calculator');
    fetch(`${BACKEND_URL}/api/districts`).then(r => r.json()).then(setDistricts);
  }, []);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/savings-calculator`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bags, competitor, location }),
      });
      setResults(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleShare = useCallback(async (type) => {
    if (!shareCardRef.current || !results) return;
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: '#0a0a0b', scale: 2, useCORS: true,
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (type === 'whatsapp') {
        const text = encodeURIComponent(results.share_lines?.hi || results.share_lines?.en || '');
        window.open(`https://wa.me/?text=${text}`, '_blank');
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'lion-cement-savings.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) { console.error(err); }
  }, [results]);

  const compLine = results ? results.comparison_line_hi : '';

  return (
    <div className="min-h-screen bg-void" data-testid="savings-calculator-page">
      <SEOHead
        title="Savings Calculator"
        description="Calculate how much you save by choosing Lion Cement over competitors. Compare pricing per bag and total project cost savings."
        path="/savings-calculator"
      />
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div className="absolute top-1/3 left-0 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 60%)' }} />
        <div className="container mx-auto px-4 md:px-8 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            {/* Step indicator */}
            <div className="flex items-center gap-3 mb-4">
              <Link to="/tools" className="text-gray-500 hover:text-white text-xs font-mono transition-colors">Smart Tools</Link>
              <span className="text-gray-700">/</span>
              <Link to="/strength-scanner" className="text-gray-500 hover:text-coral text-xs font-mono transition-colors">Step 1</Link>
              <span className="text-gray-700">/</span>
              <span className="text-mint text-xs font-mono">Step 2</span>
              <span className="text-gray-700">/</span>
              <Link to="/calculator" className="text-gray-500 hover:text-blue-400 text-xs font-mono transition-colors">Step 3</Link>
            </div>
            <span className="text-mint font-mono text-sm mb-4 block">{t.tag}</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {t.title}<br /><span className="text-mint">{t.titleAccent}</span>
            </h1>
            <p className="text-gray-400 text-base md:text-lg mt-4 max-w-xl">{t.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Inputs */}
            <div className="lg:col-span-2">
              <div className="bg-surface border border-white/5 rounded-2xl p-6 space-y-6 sticky top-28">
                {/* Bags */}
                <div>
                  <label className="font-display text-sm font-medium text-white mb-2 block">{t.bags}</label>
                  <input type="number" value={bags} onChange={e => setBags(Math.max(1, parseInt(e.target.value) || 0))} data-testid="savings-bags"
                    className="w-full bg-void border border-white/10 focus:border-mint rounded-xl px-4 py-3.5 text-white text-2xl font-display font-bold outline-none text-center" />
                  <div className="flex gap-2 mt-2">
                    {[50, 100, 200, 500].map(n => (
                      <button key={n} onClick={() => setBags(n)} className={`flex-1 text-xs py-2 rounded-lg font-display transition-all ${bags === n ? 'bg-mint/20 text-mint border border-mint/30' : 'bg-void border border-white/10 text-gray-500 hover:text-white'}`}>{n}</button>
                    ))}
                  </div>
                </div>

                {/* Competitor */}
                <div>
                  <label className="font-display text-sm font-medium text-white mb-3 block">{t.currentChoice}</label>
                  {['premium', 'popular', 'economy'].map(c => (
                    <button key={c} onClick={() => setCompetitor(c)} data-testid={`savings-comp-${c}`}
                      className={`w-full mb-2 p-3 rounded-xl text-sm font-display text-left transition-all ${competitor === c ? 'bg-mint/10 text-mint border border-mint/30' : 'bg-void border border-white/10 text-gray-400 hover:border-white/20'}`}>
                      {t[c]}
                    </button>
                  ))}
                </div>

                {/* Location */}
                <div>
                  <label className="font-display text-sm font-medium text-white mb-2 block flex items-center gap-2"><MapPin className="w-4 h-4 text-mint" /> {t.location}</label>
                  <div className="relative">
                    <select value={location} onChange={e => setLocation(e.target.value)} data-testid="savings-location"
                      className="w-full bg-void border border-white/10 focus:border-mint rounded-xl px-4 py-3 text-white outline-none appearance-none text-sm">
                      <option value="">{t.selectDistrict}</option>
                      <optgroup label="Gujarat">{(districts.Gujarat || []).map(d => <option key={d} value={d}>{d}</option>)}</optgroup>
                      <optgroup label="Rajasthan">{(districts.Rajasthan || []).map(d => <option key={d} value={d}>{d}</option>)}</optgroup>
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  </div>
                </div>

                <button onClick={handleCalculate} disabled={loading} data-testid="savings-calculate-btn"
                  className="w-full flex items-center justify-center gap-2 bg-mint hover:bg-mint/80 text-void font-display font-bold py-4 rounded-full transition-all disabled:opacity-50">
                  {loading ? <div className="w-5 h-5 border-2 border-void border-t-transparent rounded-full animate-spin" /> : <>{t.calculate} <ArrowRight className="w-5 h-5" /></>}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-3">
              <AnimatePresence mode="wait">
                {results ? (
                  <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6" data-testid="savings-results">
                    {/* Big Savings Display */}
                    <div className="bg-surface border border-mint/20 rounded-2xl p-8 text-center">
                      <p className="text-gray-400 text-sm mb-2">{t.youSave}</p>
                      <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 10 }}
                        className="text-5xl md:text-7xl font-display font-black text-mint mb-2">
                        Rs.{results.total_saved?.toLocaleString()}
                      </motion.div>
                      <p className="text-gray-300 text-base">{compLine}</p>
                      {results.extra_bags > 0 && (
                        <div className="mt-4 inline-flex items-center gap-2 bg-mint/10 text-mint px-4 py-2 rounded-full text-sm font-display">
                          +{results.extra_bags} {t.extraBags}
                        </div>
                      )}
                    </div>

                    {/* Price Comparison */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-surface border border-mint/20 rounded-xl p-5 text-center">
                        <p className="text-gray-500 text-xs mb-1">{t.lionPrice}</p>
                        <p className="text-mint text-3xl font-display font-bold">Rs.{results.lion_price}</p>
                        <p className="text-gray-600 text-xs">{t.pricePerBag}</p>
                      </div>
                      <div className="bg-surface border border-white/10 rounded-xl p-5 text-center">
                        <p className="text-gray-500 text-xs mb-1">{t.compPrice}</p>
                        <p className="text-gray-300 text-3xl font-display font-bold">Rs.{results.competitor_price}</p>
                        <p className="text-gray-600 text-xs">{t.pricePerBag}</p>
                      </div>
                    </div>

                    {/* Shareable Card */}
                    <div ref={shareCardRef} className="bg-gradient-to-br from-surface to-void border border-white/10 rounded-2xl p-8 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-coral/10" />
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                          <img src="/logo.png" alt="Avi Cement" className="h-6" />
                          <span className="text-white font-display font-bold text-lg">Lion Cement</span>
                        </div>
                        <p className="text-white text-xl md:text-2xl font-display font-bold leading-snug mb-4">
                          "{results.share_lines?.hi || results.share_lines?.en}"
                        </p>
                        <div className="flex items-center gap-4">
                          <span className="text-mint text-4xl font-display font-black">Rs.{results.total_saved?.toLocaleString()}</span>
                          <span className="text-gray-500 text-sm">ki bachat</span>
                        </div>
                      </div>
                    </div>

                    {/* Share Actions */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button onClick={() => handleShare('whatsapp')} data-testid="savings-share-whatsapp"
                        className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white font-display font-medium py-4 rounded-full transition-all">
                        <MessageCircle className="w-5 h-5" /> {t.shareWhatsApp}
                      </button>
                      <button onClick={() => handleShare('download')} data-testid="savings-download-card"
                        className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-display px-6 py-4 rounded-full transition-all">
                        <Download className="w-4 h-4" /> {t.downloadCard}
                      </button>
                    </div>

                    {/* CTA */}
                    <Link to="/locations" data-testid="savings-find-dealer"
                      className="flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium py-4 rounded-full transition-all w-full">
                      {t.findDealer} <ArrowRight className="w-5 h-5" />
                    </Link>

                    {/* Next step flow */}
                    <div className="bg-surface-light border border-white/5 rounded-2xl p-5 flex items-center justify-between" data-testid="savings-flow-next">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 font-display font-bold text-xs">3</div>
                        <div>
                          <p className="text-white text-sm font-display font-medium">Next: Project Calculator</p>
                          <p className="text-gray-500 text-xs">Sabhi materials ka anumaan lagayein</p>
                        </div>
                      </div>
                      <Link to="/calculator" className="text-blue-400 text-sm font-display flex items-center gap-1 hover:gap-2 transition-all">Jao <ArrowRight className="w-4 h-4" /></Link>
                    </div>

                    <p className="text-gray-600 text-xs text-center">{t.disclaimer}</p>
                  </motion.div>
                ) : (
                  <div className="bg-surface border border-white/5 rounded-2xl p-12 text-center" data-testid="savings-empty">
                    <div className="w-20 h-20 rounded-2xl bg-mint/10 flex items-center justify-center mx-auto mb-6">
                      <span className="text-4xl font-display font-black text-mint">Rs.</span>
                    </div>
                    <p className="text-gray-400 text-base font-display">Details daalein aur dekhein kitna bachate hain!</p>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SavingsCalculatorPage;
